import { Button } from "@/components/ui/button";
import { ConsentForm } from "@/features/consent/ConsentForm";
import ConsentSwitch from "@/features/consent/ConsentSwitch";
import {
	consentKeys,
	fetchConsentById,
	useSuspenseConsentById,
} from "@/features/consent/api/selectConsentById";
import { useNavigateAndPrint } from "@/features/previews/hooks/useNavigateAndPrint";
import type { ConsentSignature, ConsentViewT } from "@/types/database/consents";
import { createFileRoute } from "@tanstack/react-router";
import { PrinterIcon } from "lucide-react";
import { useCallback } from "react";

export const Route = createFileRoute("/preview/consent/$consentId")({
	component: ConsentFormView,
	loader: async ({ context: { queryClient }, params }) => {
		const { consentId } = params;

		// Prefetch the consent data for suspense
		await queryClient.ensureQueryData({
			queryKey: consentKeys.byId(consentId),
			queryFn: () => fetchConsentById(consentId),
		});

		return { consentId };
	},
});

function ConsentFormView() {
	const { consentId } = Route.useParams();
	const { data: consent } = useSuspenseConsentById(consentId);
	const { navigateAndPrint, isPrinting } = useNavigateAndPrint();

	// Return early if we don't have consent data
	if (!consent) {
		return (
			<div className="flex flex-col justify-center items-center p-4 min-h-screen">
				<h1 className="mb-4 font-bold text-2xl">Consent Not Found</h1>
				<p className="mb-6 text-muted-foreground">
					The consent form you are looking for could not be found.
				</p>
				<Button variant="outline" onClick={() => window.history.back()}>
					Go Back
				</Button>
			</div>
		);
	}

	// Create a valid ConsentViewT with default values for missing properties
	const fullConsent: ConsentViewT = {
		...consent,
		signatures: [],
		has_signatures: false,
		// If the API returns signatures, use them
		...(typeof (consent as unknown as Record<string, unknown>).signatures !==
			"undefined" && {
			signatures: (consent as unknown as Record<string, unknown>)
				.signatures as ConsentSignature[],
			has_signatures: true,
		}),
	};

	const handlePrint = useCallback(() => {
		// Use Doppio for printing instead of browser print
		navigateAndPrint({
			to: "/preview/consent/$consentId",
			params: { consentId },
			search: { print: "true" },
		});
	}, [consentId, navigateAndPrint]);

	return (
		<>
			<Button
				variant="outline"
				size="sm"
				className="top-8 right-8 z-10 fixed print-button"
				onClick={handlePrint}
				disabled={isPrinting}
			>
				<PrinterIcon className="mr-2 w-4 h-4" />
				{isPrinting ? "Printing..." : "Print"}
			</Button>
			<div className="consent-form">
				<ConsentSwitch consentKey={consent.consent_key} />
				<ConsentForm
					diaryId={consent.diary_id as string}
					consent={fullConsent}
					onSignSuccess={() => {
						// Optionally add success handling logic here
					}}
				/>
			</div>
		</>
	);
}
